// ============================================================================
// Product Types - WholesaleHub Product System
// ============================================================================

// ----------------------------------------------------------------------------
// Product Categories & Enums
// ----------------------------------------------------------------------------

export type ProductCategory =
  | 'Lighting'
  | 'Engine'
  | 'Chassis'
  | 'Brakes'
  | 'Electrical'
  | 'Drivetrain'
  | 'Suspension'
  | 'Cooling'
  | 'Filters'
  | 'Seals'
  | 'Body'
  | 'Interior'
  | 'Exhaust'
  | 'Fuel System'
  | 'HVAC'
  | 'Safety'
  | 'Tools'
  | 'Accessories'

export type ProductSubCategory =
  | 'LED'
  | 'Caps'
  | 'Fasteners'
  | 'Bearings'
  | 'Bolt Kit'
  | 'Strobe'
  | 'Marker'
  | 'Headlight'
  | 'Tail Light'
  | 'Work Light'
  | 'Hydraulic Cap'
  | 'Tarp Bearing'
  | 'Air Brake'
  | 'Disc Brake'
  | 'Drum Brake'
  | 'Alternator'
  | 'Starter'
  | 'Battery'
  | 'Wiring'
  | 'Gear Set'
  | 'Axle'
  | 'U-Joint'
  | 'Air Bag'
  | 'Leaf Spring'
  | 'Shock'
  | 'Bushing'
  | 'Fan Clutch'
  | 'Radiator'
  | 'Water Pump'
  | 'Thermostat'
  | 'Fuel Filter'
  | 'Oil Filter'
  | 'Air Filter'
  | 'Hydraulic Filter'
  | 'Hub Seal'
  | 'Wheel Seal'
  | 'Gasket'
  | 'O-Ring'
  | string // Allow custom sub-categories

export type ProductType =
  | 'Strobe'
  | 'Marker'
  | 'Headlight'
  | 'Hydraulic Cap'
  | 'Bolt'
  | 'Bolt Kit'
  | 'Tarp Bearing'
  | 'Brake Disc'
  | 'Brake Drum'
  | 'Actuator'
  | 'Alternator'
  | 'Gear Set'
  | 'Hub Seal'
  | 'Air Bag'
  | 'Fuel Filter'
  | 'LED Light'
  | 'Fan Clutch'
  | string // Allow custom types

export type Manufacturer =
  | 'Maxxima'
  | 'Buyers'
  | 'Dayton'
  | 'Automann'
  | 'Hendrickson'
  | 'Wabco'
  | 'Delco'
  | 'Meritor'
  | 'Stemco'
  | 'Fleetguard'
  | 'TruckLite'
  | 'Bendix'
  | 'Grote'
  | 'Dorman'
  | 'SKF'
  | 'Timken'
  | 'ConMet'
  | 'Webb'
  | 'Haldex'
  | 'SAF-Holland'
  | string // Allow custom manufacturers

// ----------------------------------------------------------------------------
// Core Product Interface
// ----------------------------------------------------------------------------

export interface Product {
  id: string
  name: string
  category: ProductCategory | string
  manufacturer: Manufacturer | string
  description: string
  sku: string
  image: string
  isFeatured: boolean
  // Optional fields for extended product info
  price?: string
  originalPrice?: string  // For sale items - shows crossed out original price
  summary?: string
  details?: string
  specifications?: ProductSpecification[]
  relatedProducts?: string[] // Array of product IDs
  createdAt?: string
  updatedAt?: string
}

export interface ProductSpecification {
  label: string
  value: string
}

// ----------------------------------------------------------------------------
// Featured Product System Types
// ----------------------------------------------------------------------------

export interface FeaturedProductSlot {
  id: number // 1-10
  productId: string | null
  product?: Product
  isActive: boolean
  displayOrder: number
}

export interface FeaturedProductConfig {
  slots: FeaturedProductSlot[]
  updatedAt: string
  updatedBy: string
}

// ----------------------------------------------------------------------------
// API Response Types
// ----------------------------------------------------------------------------

export interface ProductListResponse {
  products: Product[]
  total: number
  page: number
  pageSize: number
}

export interface ProductSearchParams {
  query?: string
  category?: ProductCategory | string
  manufacturer?: Manufacturer | string
  isFeatured?: boolean
  page?: number
  pageSize?: number
}

// ----------------------------------------------------------------------------
// Sample/Mock Data for Development
// ----------------------------------------------------------------------------

export const SAMPLE_PRODUCTS: Product[] = [
  {
    id: '1',
    name: 'Air Brakes',
    category: 'Brakes',
    manufacturer: 'Hendrickson',
    description: 'Premium heavy-duty brake disc for trucks and trailers.',
    sku: 'HND-BD15',
    image: '/img/parts/parts-disc.png',
    isFeatured: true,
    price: '$349',
    summary: 'Premium heavy-duty brake disc for trucks and trailers.',
    details: 'High-strength steel construction, precision machined for reduced vibration. Fits most heavy-duty axles.'
  },
  {
    id: '2',
    name: 'Actuator Assembly',
    category: 'Brakes',
    manufacturer: 'Wabco',
    description: 'High-performance actuator assembly for air brake systems.',
    sku: 'WAB-ACT100',
    image: '/img/parts/parts-actuator.png',
    isFeatured: true,
    price: '$599',
    summary: 'High-performance actuator assembly for air brake systems.',
    details: 'Includes mounting hardware and a 1-year limited warranty.'
  },
  {
    id: '3',
    name: 'Alternator',
    category: 'Electrical',
    manufacturer: 'Delco',
    description: 'High-efficiency alternator for heavy-duty applications.',
    sku: 'DLC-ALT200',
    image: '/img/parts/parts-alt.png',
    isFeatured: true,
    price: '$199',
    summary: 'Alternative part for custom builds and repairs.',
    details: 'Compact design, high-efficiency output and long lifecycle.'
  },
  {
    id: '4',
    name: 'Gear Set',
    category: 'Drivetrain',
    manufacturer: 'Meritor',
    description: 'Precision gear set for maximum drivetrain efficiency.',
    sku: 'MER-GS450',
    image: '/img/parts/parts-gear.png',
    isFeatured: true,
    price: '$899',
    summary: 'Precision gear set for maximum drivetrain efficiency.',
    details: 'Hardened alloy gears, precision-ground for quiet operation and long life.'
  },
  {
    id: '5',
    name: 'Hubcap Seal',
    category: 'Seals',
    manufacturer: 'Stemco',
    description: 'Universal replacement hubcap seal.',
    sku: 'STM-HCS100',
    image: '/img/parts/parts-hub.png',
    isFeatured: false,
    price: '$29',
    summary: 'Universal replacement hubcap seal.',
    details: 'Durable rubber compound, easy installation, reduces contamination risk.'
  },
  {
    id: '6',
    name: 'Air Bag Suspension',
    category: 'Suspension',
    manufacturer: 'Meritor',
    description: 'Heavy-duty suspension system designed for rugged use.',
    sku: 'MER-ABS600',
    image: '/img/parts/parts-suspension.png',
    isFeatured: true,
    price: '$1,299',
    summary: 'Heavy-duty suspension system designed for rugged use.',
    details: 'Optimized for load balancing and ride comfort with reinforced mounting.'
  },
  {
    id: '7',
    name: 'Fuel Filters',
    category: 'Filters',
    manufacturer: 'Fleetguard',
    description: 'High-performance fuel filter for cleaner fuel delivery.',
    sku: 'FLG-FF100',
    image: '/img/parts/parts-fuel_filter.png',
    isFeatured: false,
    price: '$24',
    summary: 'High-performance fuel filter for cleaner fuel delivery.',
    details: 'Multi-stage filtration removes particulates and water for improved engine life.'
  },
  {
    id: '8',
    name: 'LED Lights',
    category: 'Lighting',
    manufacturer: 'TruckLite',
    description: 'Energy-efficient LED headlight for improved visibility.',
    sku: 'TRL-LED700',
    image: '/img/parts/parts-lighting.png',
    isFeatured: true,
    price: '$79',
    summary: 'Energy-efficient LED headlight for improved visibility.',
    details: 'IP68 rated, long-life LEDs, high lumen output with reduced draw.'
  },
  {
    id: '9',
    name: 'Fan Clutch',
    category: 'Cooling',
    manufacturer: 'Bendix',
    description: 'Durable fan clutch for efficient cooling system operation.',
    sku: 'BDX-FC100',
    image: '/img/parts/parts-fan_clutch.png',
    isFeatured: true,
    price: '$159',
    summary: 'Durable fan clutch for efficient cooling system operation.',
    details: 'Precision-balanced, improves cooling response and reduces noise.'
  }
]

// Default featured product configuration (10 slots)
export const DEFAULT_FEATURED_PRODUCT_CONFIG: FeaturedProductConfig = {
  slots: Array.from({ length: 10 }, (_, i) => ({
    id: i + 1,
    productId: SAMPLE_PRODUCTS[i]?.id || null,
    product: SAMPLE_PRODUCTS[i] || undefined,
    isActive: i < SAMPLE_PRODUCTS.length,
    displayOrder: i + 1
  })),
  updatedAt: new Date().toISOString(),
  updatedBy: 'system'
}
